import React, { useState } from "react";
import { Modal, Button, Form, Badge, Table } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faDownload } from "@fortawesome/free-solid-svg-icons";
import "react-toastify/dist/ReactToastify.css";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ImportLeads = (props) => {
  const [file, setFile] = useState(null);
  const [isSpinner, setIsSpinner] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [invalidRows, setInvalidRows] = useState([]);
  const [resultFilter, setResultFilter] = useState("all"); // all | inserted | updated | skipped | invalid
  const [duplicateMode, setDuplicateMode] = useState("upsert"); // upsert | insert_only
  const [tagMode, setTagMode] = useState("add"); // add | replace (applies on update)

  const csvData = [
    [
      "First Name",
      "Last Name",
      "Country Code",
      "WhatsApp Number",
      "Email",
      "Status",
      "DOB (DD-MM-YYYY)",
      "Address",
      "Tag Names (comma-separated)",
      "Description",
    ],
    [
      "John",
      "Doe",
      "91",
      "9876XXXXXX",
      "john@example.com",
      "Open - Not Contacted",
      "28-05-1995",
      "Ajmer",
      "Hot lead, Warm lead",
      "Interested in product X",
    ],
  ];

  // Normalize header string: lowercase, remove parentheses, trim, collapse spaces
  const normalizeHeader = (header) =>
    header
      .toLowerCase()
      .replace(/[\(\)]/g, " ")
      // replace punctuation with spaces so "DD-MM-YYYY" -> "dd mm yyyy"
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const convertToCSV = (array) =>
    array
      .map((row) =>
        row
          .map((value) =>
            typeof value === "string" && value.includes(",")
              ? `"${value}"`
              : value,
          )
          .join(","),
      )
      .join("\n");

  const downloadCSV = () => {
    const csvString = convertToCSV(csvData);
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "lead_records.csv");
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      const validExtensions = ["xls", "xlsx", "csv"];
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrorMessage("Only .csv, .xls, and .xlsx files are allowed.");
        setFile(null);
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        const rows = content
          .split("\n")
          .map((row) => row.trim())
          .filter((r) => r.length > 0);

        if (rows.length === 0) {
          setErrorMessage("The file is empty.");
          setFile(null);
          return;
        }

        // Normalize headers from the first row
        const headers = rows[0]
          .split(",")
          .map((header) => normalizeHeader(header));

        // Required headers normalized (after normalizeHeader)
        const requiredHeaders = [
          "first name",
          "last name",
          "country code",
          "whatsapp number",
          "email",
          "dob dd mm yyyy",
          "address",
          "tag names comma separated",
          "description",
        ];

        const missingHeaders = requiredHeaders.filter(
          (h) => !headers.includes(h),
        );

        if (missingHeaders.length > 0) {
          setErrorMessage(
            `Invalid file format. Missing headers: ${missingHeaders.join(", ")}`,
          );
          setFile(null);
          e.target.value = "";
          return;
        }

        setFile(selectedFile);
        setErrorMessage("");
      };

      reader.readAsText(selectedFile);
    }
  };

  const readExcelFile = async (file) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (!jsonData.length) {
        toast.error("The file is empty.");
        setIsSpinner(false);
        return;
      }

      const rawHeaders = jsonData[0];
      // Normalize all headers for comparison
      const formattedHeaders = rawHeaders.map((header) =>
        normalizeHeader(header || ""),
      );

      const headerMapping = {
        // optional columns
        status: "leadstatus",
        "lead status": "leadstatus",

        "first name": "firstname",
        "last name": "lastname",
        "country code": "country_code",
        "whatsapp number": "whatsapp_number",
        email: "email",
        "dob dd mm yyyy": "dob",
        address: "address",
        "tag names comma separated": "tag_names",
        description: "description",
      };

      // Check for missing required headers
      // Only validate required headers. Optional columns may be absent.
      const missingHeaders = [
        "first name",
        "last name",
        "country code",
        "whatsapp number",
        "email",
        "dob dd mm yyyy",
        "address",
        "tag names comma separated",
        "description",
      ].filter((h) => !formattedHeaders.includes(h));
      if (missingHeaders.length > 0) {
        toast.error(`Missing required headers: ${missingHeaders.join(", ")}`);
        setIsSpinner(false);
        return;
      }

      // Map headers to their index in the row
      const headerIndexMap = {};
      formattedHeaders.forEach((header, index) => {
        if (headerMapping[header]) {
          headerIndexMap[headerMapping[header]] = index;
        }
      });

      // Parse rows to objects
      const finalJson = jsonData.slice(1).map((row) => {
        const rowData = {};
        // include only keys that exist in the uploaded sheet (optional columns won't be present)
        Object.values(headerMapping).forEach((key) => {
          const idx = headerIndexMap[key];
          if (idx !== undefined) {
            rowData[key] = row[idx] !== undefined ? row[idx] : "";
          }
        });
        return rowData;
      });

      // Client-side validation: don't send invalid rows to backend.
      const invalidLeads = [];
      const validLeads = [];
      for (const lead of finalJson) {
        const hasName =
          (lead.firstname != null && String(lead.firstname).trim()) ||
          (lead.lastname != null && String(lead.lastname).trim());
        const whatsapp = lead.whatsapp_number?.toString().trim();
        const whatsappValid = whatsapp && /^\d+$/.test(whatsapp);

        if (!hasName || !whatsappValid) {
          invalidLeads.push({
            lead,
            reason: "invalid_row",
            message:
              "Missing first/last name or WhatsApp number is empty/invalid (digits only).",
          });
        } else {
          validLeads.push(lead);
        }
      }
      setInvalidRows(invalidLeads);

      if (validLeads.length > 0) {
        try {
          const response = await WhatsAppAPI.importLeads({
            leads: validLeads,
            options: {
              duplicateMode,
              tagMode,
            },
          });
          if (response.success) {
            setImportResult(response);
            const inserted = Array.isArray(response.createdLeads)
              ? response.createdLeads.length
              : 0;
            const updated = Array.isArray(response.updatedLeads)
              ? response.updatedLeads.length
              : 0;
            const skipped = Array.isArray(response.skippedLeads)
              ? response.skippedLeads.length
              : 0;
            toast.success(
              `Import done. Inserted ${inserted}, Updated ${updated}, Skipped ${skipped}.`,
            );
          } else {
            toast.error("Failed to import leads. Please check the data.");
          }
        } catch (error) {
          // console.error("Error importing leads:", error);
          toast.error("An error occurred during import.");
        } finally {
          setIsSpinner(false);
        }
      } else {
        setImportResult({
          success: true,
          message: "No valid leads to import.",
          createdLeads: [],
          updatedLeads: [],
          skippedLeads: [],
        });
        toast.warn("No valid leads found in the file.");
        setIsSpinner(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleFileSubmit = () => {
    if (file) {
      setImportResult(null);
      setInvalidRows([]);
      setResultFilter("all");
      setIsSpinner(true);
      readExcelFile(file);
    }
  };

  const combinedRows = (() => {
    if (!importResult) return [];

    // Use a dedicated field to avoid clashing with any existing `status` property in lead objects.
    const inserted = (importResult.createdLeads || []).map((l) => ({
      ...l,
      import_status: "Inserted",
      reason: "",
    }));
    const updated = (importResult.updatedLeads || []).map((l) => ({
      ...l,
      import_status: "Updated",
      reason: "",
    }));
    const skipped = (importResult.skippedLeads || []).map((x) => {
      const lead = x?.lead || x || {};
      return {
        ...lead,
        import_status: "Skipped",
        reason: x?.reason || "",
        message: x?.message || "",
      };
    });
    const invalid = (invalidRows || []).map((x) => {
      const lead = x?.lead || {};
      return {
        ...lead,
        import_status: "Invalid",
        reason: x?.reason || "",
        message: x?.message || "",
      };
    });

    return [...inserted, ...updated, ...skipped, ...invalid];
  })();

  const rowsToShow = (() => {
    if (!importResult) return [];
    const rows = combinedRows;
    if (resultFilter === "inserted")
      return rows.filter((r) => r.import_status === "Inserted");
    if (resultFilter === "updated")
      return rows.filter((r) => r.import_status === "Updated");
    if (resultFilter === "skipped")
      return rows.filter((r) => r.import_status === "Skipped");
    if (resultFilter === "invalid")
      return rows.filter((r) => r.import_status === "Invalid");
    return rows;
  })();

  const formatTags = (tagNames) => {
    if (Array.isArray(tagNames)) {
      return tagNames
        .map((t) => (typeof t === "string" ? t : t?.name))
        .filter(Boolean)
        .join(", ");
    }
    return tagNames || "";
  };

  const downloadResultsCSV = () => {
    const headers = [
      "Import Status",
      "First Name",
      "Last Name",
      "Country Code",
      "WhatsApp Number",
      "Email",
      "Lead Status",
      "DOB",
      "Address",
      "Tag Names",
      "Description",
      "Reason",
    ];

    const escapeCsv = (v) => {
      const s = v == null ? "" : String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const rows = rowsToShow.map((r) => [
      r.import_status || "",
      r.firstname || "",
      r.lastname || "",
      r.country_code || "",
      r.whatsapp_number || "",
      r.email || "",
      r.leadstatus || "",
      r.dob || "",
      r.address || "",
      formatTags(r.tag_names),
      r.description || "",
      r.import_status === "Skipped" || r.import_status === "Invalid"
        ? r.message || r.reason || r.import_status
        : "",
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const label =
      resultFilter === "all" ? "all" : String(resultFilter).toLowerCase();
    saveAs(blob, `lead_import_${label}.csv`);
  };

  const onClose = () => {
    setFile(null);
    setErrorMessage("");
    setImportResult(null);
    setInvalidRows([]);
    setResultFilter("all");
    props.refreshData();
  };

  return (
    <div>
      <Modal
        show={props.show}
        onHide={importResult ? onClose : props.onHide}
        centered
        size={importResult ? "xl" : "md"}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Import Leads</Modal.Title>
        </Modal.Header>

        {!isSpinner && !importResult ? (
          <>
            <Modal.Body>
              <div className="p-3 rounded">
                <Form.Group controlId="formFile">
                  <Form.Label>
                    <strong>
                      <FontAwesomeIcon icon={faUpload} /> Upload File
                    </strong>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                  />
                  {errorMessage && (
                    <Form.Text className="text-danger mt-1 d-block">
                      {errorMessage}
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    <ul className="mt-3 ps-3 mb-0 small">
                      <li>
                        <strong>DOB</strong> should be <code>DD-MM-YYYY</code>
                      </li>
                      <li>
                        <strong>Tags</strong> must be comma-separated like{" "}
                        <code>Hot lead, Warm lead</code>
                      </li>
                      <li>
                        Headers required: <br />
                        <code>
                          First Name, Last Name, Country Code, WhatsApp Number,
                          Email, DOB (DD-MM-YYYY), Address, Tag Names
                          (comma-separated), Description
                        </code>
                      </li>
                    </ul>
                  </Form.Text>
                </Form.Group>

                <hr className="my-3" />

                <div className="mb-3">
                  <div className="fw-semibold mb-2">Duplicate handling</div>
                  <Form.Check
                    type="radio"
                    id="dup-upsert"
                    name="duplicateMode"
                    value="upsert"
                    checked={duplicateMode === "upsert"}
                    onChange={(e) => setDuplicateMode(e.target.value)}
                    label={
                      <span>
                        <strong>Update all (Upsert)</strong> — if WhatsApp number
                        already exists, it will be updated from the file.
                      </span>
                    }
                    className="mb-1"
                  />
                  <Form.Check
                    type="radio"
                    id="dup-insert-only"
                    name="duplicateMode"
                    value="insert_only"
                    checked={duplicateMode === "insert_only"}
                    onChange={(e) => setDuplicateMode(e.target.value)}
                    label={
                      <span>
                        <strong>Insert only</strong> — duplicates will be skipped
                        (no updates).
                      </span>
                    }
                  />
                  <Form.Text className="text-muted">
                    When <strong>Update all</strong> is selected, duplicate WhatsApp
                    rows may overwrite existing values (based on non-empty cells
                    in the file).
                  </Form.Text>
                </div>

                <div className="mb-1">
                  <div className="fw-semibold mb-2">Tags on update</div>
                  <Form.Check
                    type="radio"
                    id="tag-add"
                    name="tagMode"
                    value="add"
                    checked={tagMode === "add"}
                    onChange={(e) => setTagMode(e.target.value)}
                    label={
                      <span>
                        <strong>Add more tags</strong> — keep existing tags and
                        merge tags from the file (duplicates ignored).
                      </span>
                    }
                    className="mb-1"
                    disabled={duplicateMode !== "upsert"}
                  />
                  <Form.Check
                    type="radio"
                    id="tag-replace"
                    name="tagMode"
                    value="replace"
                    checked={tagMode === "replace"}
                    onChange={(e) => setTagMode(e.target.value)}
                    label={
                      <span>
                        <strong>Replace tags</strong> — remove old tags and set
                        only the tags from the file.
                      </span>
                    }
                    disabled={duplicateMode !== "upsert"}
                  />
                  {duplicateMode !== "upsert" && (
                    <Form.Text className="text-muted">
                      Tag mode applies only when <strong>Update all</strong> is selected.
                    </Form.Text>
                  )}
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between align-items-center">
              <Button variant="outline-primary" onClick={downloadCSV}>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download Dummy File
              </Button>

              <div>
                <Button variant="light" onClick={props.onHide} className="me-3">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleFileSubmit}
                  disabled={!file}
                >
                  Submit
                </Button>
              </div>
            </Modal.Footer>
          </>
        ) : isSpinner ? (
          <div className="p-5 d-flex justify-content-center align-items-center flex-column">
            <div className="sk-cube-grid mb-3">
              {[...Array(9)].map((_, i) => (
                <div key={i} className={`sk-cube sk-cube${i + 1}`}></div>
              ))}
            </div>
            <span>Importing leads, please wait...</span>
          </div>
        ) : (
          <>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <Badge bg="success" className="px-3 py-2">
                  Inserted:{" "}
                  {(importResult?.createdLeads || []).length}
                </Badge>
                <Badge bg="primary" className="px-3 py-2">
                  Updated:{" "}
                  {(importResult?.updatedLeads || []).length}
                </Badge>
                <Badge bg="secondary" className="px-3 py-2">
                  Skipped:{" "}
                  {(importResult?.skippedLeads || []).length}
                </Badge>
                <Badge bg="warning" text="dark" className="px-3 py-2">
                  Invalid: {invalidRows.length}
                </Badge>

                <div className="text-muted small ms-2">
                  <span className="me-2">
                    <strong>Duplicates:</strong>{" "}
                    {duplicateMode === "upsert" ? "Update all (Upsert)" : "Insert only"}
                  </span>
                  <span>
                    <strong>Tags on update:</strong>{" "}
                    {duplicateMode === "upsert"
                      ? tagMode === "replace"
                        ? "Replace"
                        : "Add"
                      : "—"}
                  </span>
                </div>

                <div className="ms-auto d-flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    type="button"
                    variant={resultFilter === "all" ? "dark" : "outline-dark"}
                    onClick={() => setResultFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant={
                      resultFilter === "inserted"
                        ? "success"
                        : "outline-success"
                    }
                    onClick={() => setResultFilter("inserted")}
                  >
                    Inserted
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant={
                      resultFilter === "updated"
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => setResultFilter("updated")}
                  >
                    Updated
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant={
                      resultFilter === "skipped"
                        ? "secondary"
                        : "outline-secondary"
                    }
                    onClick={() => setResultFilter("skipped")}
                  >
                    Skipped
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant={
                      resultFilter === "invalid"
                        ? "warning"
                        : "outline-warning"
                    }
                    onClick={() => setResultFilter("invalid")}
                  >
                    Invalid
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant="outline-dark"
                    onClick={downloadResultsCSV}
                    title="Download CSV for the current view"
                  >
                    <FontAwesomeIcon icon={faDownload} className="me-1" />
                    Download CSV
                  </Button>
                </div>
              </div>

              <div className="table-responsive">
                <Table bordered hover size="sm" className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 110 }}>Status</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th style={{ width: 120 }}>Country</th>
                      <th style={{ width: 160 }}>WhatsApp</th>
                      <th>Email</th>
                      <th style={{ width: 170 }}>Lead Status</th>
                      <th style={{ width: 120 }}>DOB</th>
                      <th style={{ minWidth: 180 }}>Address</th>
                      <th style={{ minWidth: 200 }}>Tags</th>
                      <th style={{ minWidth: 240 }}>Description</th>
                      <th style={{ width: 240 }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsToShow.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="text-center text-muted py-4">
                          No records in this view.
                        </td>
                      </tr>
                    ) : (
                      rowsToShow.map((row, idx) => (
                        <tr
                          key={`${row.import_status}-${row.id || row.whatsapp_number || idx}`}
                        >
                          <td>
                            <Badge
                              bg={
                                row.import_status === "Inserted"
                                  ? "success"
                                  : row.import_status === "Updated"
                                    ? "primary"
                                    : row.import_status === "Invalid"
                                      ? "warning"
                                      : "secondary"
                              }
                              text={
                                row.import_status === "Invalid"
                                  ? "dark"
                                  : undefined
                              }
                            >
                              {row.import_status}
                            </Badge>
                          </td>
                          <td className="text-capitalize">
                            {row.firstname || ""}
                          </td>
                          <td className="text-capitalize">
                            {row.lastname || ""}
                          </td>
                          <td>{row.country_code || ""}</td>
                          <td>{row.whatsapp_number || ""}</td>
                          <td>{row.email || ""}</td>
                          <td>{row.leadstatus || ""}</td>
                          <td>{row.dob || ""}</td>
                          <td>{row.address || ""}</td>
                          <td>
                            {formatTags(row.tag_names)}
                          </td>
                          <td>{row.description || ""}</td>
                          <td>
                            {row.import_status === "Skipped" ||
                            row.import_status === "Invalid"
                              ? row.message || row.reason || row.import_status
                              : ""}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <div className="text-muted small">
                Tip: If a row is skipped, the reason is shown in the last
                column.
              </div>
              <Button variant="primary" onClick={onClose}>
                Done
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
      <ToastContainer autoClose={30000} />
    </div>
  );
};

export default ImportLeads;
